import { Observable, Subject, Subscription, ReplaySubject, of } from 'rxjs';
import { filter, takeUntil, takeWhile } from 'rxjs/operators';
import { ToastService } from '../../shared/components/toast/services/toast.service';
import { ChatApi } from '../apis/chat.api';
import { IChatInputMessage } from '../interfaces/chat-input.interface';
import { IChatMessage } from '../interfaces/chat-message.interface';
import { IUserAttachment } from '../interfaces/user-attachment.interface';
import {
  EnumHITLErrorCode,
  IHITLDecision,
  IHITLRequest,
  MAX_HITL_DECISIONS,
  toHITLResponse
} from '../interfaces/hitl.interface';
import { EnumAyaEventType } from '../enums/aya-event-type.enum';
import {
  EnumConversationState,
  toConversationState
} from '../enums/conversation-state.enum';
import { EnumRunControlState } from '../enums/run-control-state.enum';
import { EnumRunPhase } from '../enums/run-phase.enum';
import {
  IActiveExecution,
  toActiveExecution
} from '../interfaces/active-execution.interface';
import { IActivity } from '../interfaces/activity.interface';
import {
  IAyaEnvelope,
  isTerminalEvent,
  revisionOf
} from '../interfaces/aya-stream.interface';
import { IStreamMessageRequest } from '../interfaces/stream-message-request.interface';
import { reconcileMessages } from '../utils/reconcile-messages.util';
import { buildHistoryMessages } from '../utils/to-chat-message.util';
import { ChatHistoryStateService } from './chat-history-state.service';
import { ChatSessionSlice } from './chat-sessions.store';
import { ConversationMessageProjection } from './conversation-message-projection';
import { ConversationErrorPresenter } from './conversation-error-presenter';
import { KnowledgeCacheService } from './knowledge-cache.service';
import { MarkdownFormatterService } from './markdown-formatter.service';
import { IAyaStreamError, IAyaStreamEvent } from './server-sent-events.service';

/** Controla os recursos de execução de uma única conversa. */
export class ConversationExecution {
  private readonly destroy$ = new Subject<void>();
  private readonly resources = new Subscription();
  private readonly projection: ConversationMessageProjection;
  private readonly errors: ConversationErrorPresenter;
  private activeRunId: string | null = null;
  private stream: Subscription | null = null;
  private generation = 0;
  private disposed = false;
  private terminal = false;
  private recoveryAttempts = 0;
  private cursor: string | null = null;
  private lastSequence = 0;
  private pendingApproval: {
    request: IHITLRequest;
    optimisticIds: string[];
  } | null = null;
  private opening: ReplaySubject<void> | null = null;

  constructor(
    private markdownFormatter: MarkdownFormatterService,
    private chatApi: ChatApi,
    readonly sessionId: string,
    private historyState: ChatHistoryStateService,
    private toastService: ToastService,
    private knowledgeCache: KnowledgeCacheService,
    readonly state: ChatSessionSlice
  ) {
    this.projection = new ConversationMessageProjection(
      state.messages,
      markdownFormatter
    );
    this.errors = new ConversationErrorPresenter(
      state,
      this.projection,
      toastService,
      knowledgeCache
    );
  }

  private get messagesStore() {
    return this.state.messages;
  }
  private get activitiesStore() {
    return this.state.activities;
  }
  private get title$() {
    return this.state.title;
  }
  private get runControl$() {
    return this.state.runControl;
  }
  private get isTyping$() {
    return this.state.isTyping;
  }
  private get interrupt$() {
    return this.state.interrupt;
  }

  get busy(): boolean {
    return this.isTyping$.value || !!this.opening;
  }

  getInterrupt$(): Observable<IHITLRequest | null> {
    return this.interrupt$.asObservable();
  }

  recoverPendingInterrupt(): void {
    const generation = this.generation;
    this.resources.add(
      this.chatApi
        .getSessionState(this.sessionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (state) => {
            if (!this.isCurrent(generation)) {
              return;
            }
            const pending = state.pending_interrupts?.[0];
            this.interrupt$.next(
              state.should_render !== false && pending ? pending : null
            );
          },
          error: () => undefined
        })
    );
  }

  open(snapshot?: {
    messages: IChatMessage[];
    title: string | null;
    execution: IActiveExecution | null;
  }): Observable<void> {
    if (this.opening) {
      return this.opening.asObservable();
    }
    // Ocupada sem conteúdo é o caso de quem volta para a conversa que responde:
    // sair aqui deixaria a tela vazia.
    if (this.state.loaded) {
      return of(undefined);
    }
    // `busy` passa a contar esta própria abertura assim que `opening` é
    // atribuído: o que interessa aqui é se já havia resposta em curso.
    const respondendo = this.isTyping$.value;
    const result = new ReplaySubject<void>(1);
    this.opening = result;
    const apply = (data: {
      messages: IChatMessage[];
      title: string | null;
      execution: IActiveExecution | null;
    }) => {
      if (this.disposed) {
        return;
      }
      // Com resposta em curso o histórico é anterior aos deltas já exibidos.
      if (respondendo) {
        const emMemoria = this.messagesStore.getMessages();
        const reconciliadas = reconcileMessages(data.messages, emMemoria);
        // A reconciliação só guarda mensagem com conteúdo, e o balão que recebe
        // os deltas ainda está vazio.
        const aguardando = emMemoria.filter(
          (mensagem) =>
            !reconciliadas.some((item) => item.localId === mensagem.localId)
        );
        this.messagesStore.set([...reconciliadas, ...aguardando]);
      } else {
        this.setMessages(data.messages);
      }
      this.setTitle(data.title);
      this.state.loaded = true;
      this.opening = null;
      if (data.execution) {
        this.joinExecution(this.sessionId, data.execution);
      } else if (!respondendo) {
        this.recoverPendingInterrupt();
      }
      result.next();
      result.complete();
    };
    if (snapshot) {
      apply(snapshot);
    } else {
      this.resources.add(
        this.chatApi
          .fetchConversationHistory(this.sessionId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (history) => {
              // Por deeplink a conversa pode não estar na lista ainda.
              this.historyState.addOrUpdateItem({
                id: history.conversation.id,
                title: history.conversation.title,
                date: new Date(
                  history.conversation.lastMessageAt ??
                    history.conversation.createdAt
                ),
                pinned: history.conversation.isPinned ?? false,
                state: history.conversation.state,
                isGenerating:
                  history.conversation.state ===
                  EnumConversationState.Processing
              });
              apply({
                messages: buildHistoryMessages(
                  history.messages,
                  this.markdownFormatter
                ),
                title: history.conversation.title,
                execution: history.conversation.execution
              });
            },
            error: (error) => {
              this.opening = null;
              result.error(error);
            },
            // `takeUntil` encerra sem emitir: sem liberar, a próxima abertura
            // herdaria esta espera morta.
            complete: () => {
              if (this.opening === result) {
                this.opening = null;
                result.complete();
              }
            }
          })
      );
    }
    return result.asObservable();
  }

  setMessages(messages: IChatMessage[]): void {
    this.interrupt$.next(null);
    this.projection.clear();
    this.activitiesStore.clear();
    this.resetRunControl();
    this.messagesStore.set(messages);
  }

  getRunControl$(): Observable<EnumRunControlState> {
    return this.runControl$.asObservable();
  }

  /**
   * Parar é uma operação do servidor: abortar a conexão apenas desconecta o
   * consumidor, e a execução continua produzindo do outro lado.
   */
  stopRun(): void {
    const runId = this.activeRunId;
    const generation = this.generation;
    if (!runId || this.runControl$.value !== EnumRunControlState.RUNNING) {
      return;
    }
    this.runControl$.next(EnumRunControlState.STOPPING);
    this.resources.add(
      this.chatApi
        .stopRun(this.sessionId, runId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (!this.isCurrent(generation) || this.activeRunId !== runId) {
              return;
            }
            // A confirmação HTTP não substitui o evento terminal do SSE.
            if (!response.stopped) {
              this.runControl$.next(EnumRunControlState.RUNNING);
            }
          },
          error: () => {
            if (this.isCurrent(generation) && this.activeRunId === runId) {
              this.runControl$.next(EnumRunControlState.RUNNING);
            }
          }
        })
    );
  }

  getActivities$(): Observable<IActivity[]> {
    return this.activitiesStore.getActivities$();
  }

  getPhase$(): Observable<EnumRunPhase | null> {
    return this.activitiesStore.getPhase$();
  }

  getMessages$(): Observable<IChatMessage[]> {
    return this.messagesStore.getMessages$();
  }

  setTitle(title: string | null): void {
    if (this.title$.getValue() === title) {
      return;
    }
    this.title$.next(title);
  }

  getTitle$(): Observable<string | null> {
    return this.title$.asObservable();
  }

  getIsTyping$(): Observable<boolean> {
    return this.isTyping$.asObservable();
  }

  setTyping(value: boolean): void {
    this.isTyping$.next(value);
  }

  addMessage(message: IChatMessage): void {
    this.messagesStore.add(message);
  }

  sendUserMessage(message: IChatInputMessage): string {
    return this.projection.sendUserMessage(message);
  }

  discardMessage(localId: string): void {
    this.state.messages.remove([localId]);
  }

  showAgentError(message: string, messageId?: string): void {
    this.projection.showAgentError(message, messageId);
    this.setTyping(false);
  }

  streamMessage(
    sessionId: string,
    request: IStreamMessageRequest
  ): Subscription {
    if (sessionId !== this.sessionId || this.busy || this.interrupt$.value) {
      return Subscription.EMPTY;
    }
    this.state.loaded = true;
    this.recoveryAttempts = 0;
    this.cursor = null;
    this.lastSequence = 0;
    this.activeRunId = null;
    return this.connect(
      this.chatApi.streamMessage(sessionId, request),
      this.projection.addAgentPlaceholder()
    );
  }

  respondToInterrupt(decisions: IHITLDecision[]): Subscription | null {
    const current = this.interrupt$.getValue();
    const sessionId = this.sessionId;
    if (
      this.busy ||
      !current ||
      decisions.length !== current.action_requests.length ||
      !decisions.length ||
      decisions.length > MAX_HITL_DECISIONS
    ) {
      return null;
    }
    if (!sessionId) {
      return null;
    }

    const body = toHITLResponse(current.interrupt_id, decisions);

    this.interrupt$.next(null);
    this.setTyping(true);
    this.historyState.markGenerating(sessionId);

    this.pendingApproval = {
      request: current,
      optimisticIds: this.projection.resolveApproval(current, decisions)
    };

    const resumePlaceholderId = this.projection.addAgentPlaceholder();

    this.recoveryAttempts = 0;
    this.cursor = null;
    this.lastSequence = 0;
    this.activeRunId = null;
    return this.connect(
      this.chatApi.resume(sessionId, body),
      resumePlaceholderId
    );
  }

  joinExecution(sessionId: string, execution: IActiveExecution): Subscription {
    if (
      sessionId !== this.sessionId ||
      (this.stream &&
        !this.stream.closed &&
        this.activeRunId === execution.runId)
    ) {
      return this.stream || Subscription.EMPTY;
    }
    this.activeRunId = execution.runId;
    this.cursor = null;
    this.lastSequence = 0;
    this.projection.clear();
    return this.connect(
      this.chatApi.joinRun(sessionId, execution.runId),
      this.projection.adoptResponse(execution.responseMessageId)
    );
  }

  dispose(): void {
    this.disposed = true;
    this.generation++;
    this.destroy$.next();
    this.destroy$.complete();
    this.resources.unsubscribe();
    this.stream?.unsubscribe();
    this.opening?.complete();
    this.opening = null;
    this.projection.clear();
  }

  private isCurrent(generation: number): boolean {
    return !this.disposed && generation === this.generation;
  }

  private connect(
    source: Observable<IAyaStreamEvent>,
    placeholderId: string
  ): Subscription {
    this.stream?.unsubscribe();
    const generation = ++this.generation;
    this.terminal = false;
    this.setTyping(true);
    this.state.connection.next('connected');
    this.historyState.markGenerating(this.sessionId);
    const stream = new Subscription();
    this.stream = stream;
    stream.add(
      source
        .pipe(
          takeUntil(this.destroy$),
          filter(
            (event) =>
              event.envelope.sessionId === this.sessionId &&
              (!this.activeRunId ||
                event.envelope.runId === this.activeRunId) &&
              event.envelope.seq > this.lastSequence
          ),
          takeWhile((event) => !isTerminalEvent(event.envelope.type), true)
        )
        .subscribe({
          next: (event) => {
            if (!this.isCurrent(generation)) {
              return;
            }
            const envelope = event.envelope;
            if (
              envelope.sessionId !== this.sessionId ||
              (this.activeRunId && envelope.runId !== this.activeRunId)
            ) {
              return;
            }
            if (envelope.seq <= this.lastSequence) {
              return;
            }
            this.activeRunId = envelope.runId;
            this.lastSequence = envelope.seq;
            this.cursor = event.cursor;
            this.runControl$.next(
              this.runControl$.value === EnumRunControlState.STOPPING
                ? EnumRunControlState.STOPPING
                : EnumRunControlState.RUNNING
            );
            this.terminal = isTerminalEvent(envelope.type);
            this.handleStreamEvent(event, placeholderId, this.sessionId);
          },
          error: (error) => {
            stream.unsubscribe();
            if (this.isCurrent(generation)) {
              this.handleTransportError(this.sessionId, error, placeholderId);
            }
          },
          complete: () => {
            stream.unsubscribe();
            if (this.isCurrent(generation) && !this.terminal) {
              this.recover(placeholderId);
            }
          }
        })
    );
    return stream;
  }

  private recover(placeholderId: string): void {
    if (++this.recoveryAttempts > 2) {
      this.state.connection.next('disconnected');
      this.setTyping(false);
      this.runControl$.next(EnumRunControlState.IDLE);
      this.toastService.error(
        'Não foi possível acompanhar a resposta. Reabra a conversa para tentar novamente.'
      );
      this.state.loaded = false;
      return;
    }
    this.state.connection.next('recovering');
    if (this.activeRunId) {
      this.connect(
        this.chatApi.joinRun(this.sessionId, this.activeRunId, this.cursor),
        placeholderId
      );
    } else {
      this.reconcileFromHistory(this.sessionId);
    }
  }

  private handleStreamEvent(
    event: IAyaStreamEvent,
    placeholderId: string,
    sessionId: string
  ): void {
    const { envelope } = event;

    switch (envelope.type) {
      case EnumAyaEventType.RUN_STARTED:
        this.pendingApproval = null;
        // A lista pertence à execução: um turno novo não herda as atividades do anterior.
        this.activitiesStore.clear();
        this.bindResponseMessage(envelope, placeholderId);
        break;

      case EnumAyaEventType.MESSAGE_DELTA:
        this.projection.appendDelta(envelope, placeholderId);
        break;

      case EnumAyaEventType.MESSAGE_COMPLETED:
        this.projection.replaceText(envelope, placeholderId);
        break;

      case EnumAyaEventType.CONVERSATION_TITLE_UPDATED:
        this.applyTitle(envelope, sessionId);
        break;

      case EnumAyaEventType.CONVERSATION_STATE_CHANGED:
        this.applyConversationState(envelope, sessionId);
        break;

      case EnumAyaEventType.APPROVAL_REQUIRED:
        this.applyApproval(envelope);
        break;

      case EnumAyaEventType.ARTIFACT_CREATED:
        this.projection.applyArtifact(envelope, placeholderId);
        break;

      case EnumAyaEventType.RUN_FAILED:
        this.errors.present(
          typeof envelope.payload.code === 'string'
            ? envelope.payload.code
            : undefined,
          placeholderId
        );
        break;

      default:
        this.activitiesStore.apply(envelope);
        break;
    }

    if (isTerminalEvent(envelope.type)) {
      this.pendingApproval = null;
      this.activeRunId = null;
      this.state.connection.next('idle');
      this.runControl$.next(EnumRunControlState.IDLE);
      this.setTyping(false);
      this.projection.releaseResponse(placeholderId);
      if (envelope.type === EnumAyaEventType.RUN_COMPLETED) {
        this.historyState.markIdle(sessionId);
        this.historyState.moveToTop(sessionId);
      } else {
        this.historyState.markRunStopped(sessionId);
      }
    }
  }

  /** O placeholder otimista passa a responder pelo id canônico da resposta. */
  private bindResponseMessage(
    envelope: IAyaEnvelope,
    placeholderId: string
  ): void {
    this.activeRunId = envelope.runId;
    const responseMessageId = envelope.payload.response_message_id;
    if (typeof responseMessageId !== 'string' || !responseMessageId) {
      return;
    }
    this.messagesStore.assignServerId(placeholderId, responseMessageId);
  }

  /** Frames retidos de antes da migração podem vir sem revisão. */
  private applyConversationState(
    envelope: IAyaEnvelope,
    sessionId: string
  ): void {
    const state = toConversationState(envelope.payload.state);
    if (state) {
      // A revisão é do campo, não da run: é ela que ordena contra o canal.
      this.historyState.setState(sessionId, state, revisionOf(envelope));
    }
  }

  private applyTitle(envelope: IAyaEnvelope, sessionId: string): void {
    const title = envelope.payload.title;
    if (typeof title === 'string' && title) {
      this.historyState.setTitle(sessionId, title, revisionOf(envelope));
      if (this.sessionId === sessionId) {
        this.setTitle(title);
      }
    }
  }

  private applyApproval(envelope: IAyaEnvelope): void {
    const interrupt = envelope.payload as unknown as IHITLRequest;
    if (interrupt?.should_render !== false) {
      this.interrupt$.next(interrupt);
    }
  }

  /** O identificador pode ser o local otimista ou o canônico vindo de um join. */
  private resetRunControl(): void {
    this.activeRunId = null;
    this.runControl$.next(EnumRunControlState.IDLE);
  }

  private handleTransportError(
    sessionId: string,
    error: IAyaStreamError,
    placeholderId: string
  ): void {
    if (error?.code === EnumHITLErrorCode.GENERATION_IN_PROGRESS) {
      if (++this.recoveryAttempts > 2) {
        this.recover(placeholderId);
        return;
      }
      this.recoverFromConflict(sessionId, error, placeholderId);
      return;
    }
    if (error?.status === 404 || error?.status === 410) {
      this.activeRunId = null;
      this.recover(placeholderId);
      return;
    }
    if (error?.code && this.pendingApproval) {
      const pending = this.pendingApproval;
      this.messagesStore.remove([...pending.optimisticIds, placeholderId]);
      this.pendingApproval = null;
      this.setTyping(false);
      this.runControl$.next(EnumRunControlState.IDLE);
      this.state.connection.next('idle');
      this.historyState.markIdle(sessionId);
      this.errors.present(error.code);
      if (
        error.code !== EnumHITLErrorCode.STALE_INTERRUPT_ID &&
        error.code !== EnumHITLErrorCode.NO_PENDING_INTERRUPT
      ) {
        this.interrupt$.next(pending.request);
      }
      this.state.loaded = false;
      this.recoverPendingInterrupt();
      return;
    }
    if (error?.code) {
      this.setTyping(false);
      this.runControl$.next(EnumRunControlState.IDLE);
      this.state.connection.next('idle');
      this.historyState.markIdle(sessionId);
      this.projection.releaseResponse(placeholderId);
      this.errors.present(error.code, placeholderId);
      return;
    }
    this.recover(placeholderId);
  }

  private recoverFromConflict(
    sessionId: string,
    error: IAyaStreamError,
    placeholderId: string
  ): void {
    this.messagesStore.remove([placeholderId]);
    this.projection.releaseResponse(placeholderId);

    if (error.execution) {
      this.joinExecution(sessionId, toActiveExecution(error.execution));
      return;
    }

    this.reconcileFromHistory(sessionId);
  }

  /** Replay expirado não justifica outro POST: o histórico passa a ser a fonte. */
  private reconcileFromHistory(sessionId: string): void {
    const generation = this.generation;
    if (this.pendingApproval) {
      this.messagesStore.remove(this.pendingApproval.optimisticIds);
      this.pendingApproval = null;
    }
    this.resources.add(
      this.chatApi
        .fetchConversationHistory(sessionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (history) => {
            if (!this.isCurrent(generation)) {
              return;
            }
            const messages = buildHistoryMessages(
              history.messages,
              this.markdownFormatter
            );
            this.setMessages(
              reconcileMessages(messages, this.messagesStore.getMessages())
            );
            if (history.conversation.execution) {
              this.joinExecution(sessionId, history.conversation.execution);
            } else {
              this.state.connection.next('idle');
              this.setTyping(false);
              this.historyState.markIdle(sessionId);
              this.recoverPendingInterrupt();
            }
          },
          error: () => {
            if (!this.isCurrent(generation)) {
              return;
            }
            this.state.connection.next('disconnected');
            this.state.loaded = false;
            this.setTyping(false);
            this.runControl$.next(EnumRunControlState.IDLE);
          }
        })
    );
  }
}
